import { Box, Paper, styled, Typography } from '@mui/material'
import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import { noop } from '@/app/definitions'
import { ChatbotConfigs as ChatbotConfigsType } from '@/app/module/campaigns/types'

import ChatbotWelcome from './chatbot-welcome'

type Props = {
  configs: ChatbotConfigsType
  onChange: (config: ChatbotConfigsType) => void
}

const ChatbotConfigs: React.FC<Props> = ({ configs, onChange }) => {
  const handleConfigsChange = React.useCallback(
    (value: Schema) =>
      onChange({
        ...configs,
        ...value,
      }),
    [configs, onChange],
  )
  const handleWelcomeChange = React.useCallback(
    (welcome: ChatbotConfigsType['welcome']) =>
      onChange({
        ...configs,
        welcome,
      }),
    [configs, onChange],
  )

  const formConfigs: Schema = React.useMemo(
    () => ({
      model: configs.model,
      prompt: configs.prompt,
      knowledgeVectorStoreId: configs.knowledgeVectorStoreId,
      mediaVectorStoreId: configs.mediaVectorStoreId,
    }),
    [configs],
  )

  return (
    <Container variant="outlined">
      <Form
        id="chatbot-configs-form"
        defaultValues={formConfigs}
        fields={fields}
        schema={schema}
        onSubmit={noop}
        renderActions={(_, methods) => <OnChangeWatcher methods={methods} onChange={handleConfigsChange} />}
      >
        <Footer>
          <ChatbotWelcome welcome={configs.welcome} onChange={handleWelcomeChange} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Use the Tester tab to try the chatbot.
          </Typography>
        </Footer>
      </Form>
    </Container>
  )
}

const Container = styled(Paper)(() => ({
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: 16,
  overflowY: 'auto',
}))

const Footer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const OnChangeWatcher = ({
  onChange,
  methods: { subscribe, trigger },
}: {
  onChange: (value: Schema) => void
  methods: UseFormReturn<Schema>
}) => {
  React.useEffect(() => {
    const unsub = subscribe({
      formState: {
        values: true,
      },
      callback: async ({ values }) => {
        const valid = await trigger()

        if (!valid) {
          return
        }

        onChange(values)
      },
    })

    return () => unsub()
  }, [onChange, subscribe, trigger])

  return null
}

const fields = {
  model: {
    type: 'text',
    fieldProps: {
      name: 'model',
      label: 'AI Model',
      placeholder: 'Enter OpenAI model to use (e.g., gpt-5, gpt-5-nano, o3, o4-mini)',
      size: 'small',
    },
  },
  prompt: {
    type: 'text',
    fieldProps: {
      name: 'prompt',
      label: 'Chatbot Prompt',
      placeholder: "Describe the chatbot's purpose, tone, constraints...",
      multiline: true,
      minRows: 12,
    },
  },
  knowledgeVectorStoreId: {
    type: 'text',
    fieldProps: {
      name: 'knowledgeVectorStoreId',
      label: 'Knowledge Vector Store ID',
      placeholder: 'Enter knowledge vector store id',
      size: 'small',
    },
  },
  mediaVectorStoreId: {
    type: 'text',
    fieldProps: {
      name: 'mediaVectorStoreId',
      label: 'Media Vector Store ID',
      placeholder: 'Enter media vector store id',
      size: 'small',
    },
  },
} as const

const stringSchema = z.string().trim().min(1).optional().or(z.literal(''))

const schema = z.object({
  model: stringSchema,
  prompt: stringSchema,
  knowledgeVectorStoreId: stringSchema,
  mediaVectorStoreId: stringSchema,
})
type Schema = z.infer<typeof schema>

export default ChatbotConfigs
